import React, { useState, useEffect, useRef } from "react";

import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Snackbar,
} from "@mui/material";
import { Upload, Download, Analytics } from "@mui/icons-material";

import { apiGetFranchises } from "../../../services/apiGetFranchises";
import { apiGetFranchisesReport } from "../../../services/apiGetFranchisesReport";
import { apiBulkUpdateFranchises } from "../../../services/apiBulkUpdateFranchises";
import { useCommunityId } from "../../../hooks/useCommunityId";

import { Loader } from "../../../components/ui/Loader";
import { FranchiseDetailsModal } from "../../../components/CommunityFranchises/FranchiseDetailsModal";
import { FranchiseAnalyticsCards } from "../../../components/CommunityFranchises/FranchiseAnalyticsCards";
import { TopFranchisesCards } from "../../../components/CommunityFranchises/TopFranchisesCards";
import { FranchisesList } from "../../../components/CommunityFranchises/FranchisesList";
import { PreviewDialog } from "../../../components/CommunityFranchises/PreviewDialog";

export const FranchisesReport = () => {
  const fileInputRef = useRef(null);

  const [franchises, setFranchises] = useState([]);
  const [franchisesReport, setFranchisesReport] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFranchiseId, setSelectedFranchiseId] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const communityId = useCommunityId();

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setError("");
      const [franchises, report] = await Promise.all([
        apiGetFranchises(communityId),
        apiGetFranchisesReport(communityId)
      ]);
      setFranchises(franchises || []);
      setFranchisesReport(report || null);
    } catch (error) {
      console.error("Error fetching franchises:", error);
      setError("Erro ao carregar unidades");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchData();
    };
    loadData();
  }, []);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setCsvFile(file);
      handleCSVProcessing(file);
    } else {
      setSnackbar({
        open: true,
        message: "Por favor, selecione um arquivo CSV válido",
        severity: "error"
      });
    }
  };

  const handleCSVProcessing = async (file) => {
    try {
      const result = await processCSVFile(file);

      // Create preview data - only show franchises that will be modified
      const existingNames = franchises.map(f => f.name);
      const toCreate = result.franchiseNames.filter(name => !existingNames.includes(name));
      const toDeactivate = existingNames.filter(name => !result.franchiseNames.includes(name));

      setPreviewData({
        toCreate,
        toDeactivate,
        total: result.franchiseNames.length
      });
      setIsPreviewOpen(true);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message,
        severity: "error"
      });
    }
  };

  const handleUpload = async () => {
    if (!csvFile) return;

    setIsUploading(true);
    try {
      const result = await apiBulkUpdateFranchises(communityId, csvFile);

      if (result.success) {
        setSnackbar({
          open: true,
          message: `Sucesso! ${result.statistics.created} criadas, ${result.statistics.activated} ativadas, ${result.statistics.deactivated} desativadas`,
          severity: "success"
        });

        // Refresh the franchises list
        await fetchData();

        // Reset form
        setCsvFile(null);
        setPreviewData(null);
        setIsPreviewOpen(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setSnackbar({
          open: true,
          message: result.error || "Erro ao atualizar unidades",
          severity: "error"
        });
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Erro ao processar arquivo",
        severity: "error"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFranchiseClick = (franchise) => {
    setSelectedFranchiseId(franchise.id);
    setShowDetailsModal(true);
  };

  const handleCloseDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedFranchiseId(null);
  };

  const downloadCSVTemplate = () => {
    const csvContent = "name\nExemplo1\nExemplo2";
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'modelo_unidades.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (isLoading) return <Loader />;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: "bold" }}>
        Gerenciar Unidades
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Section */}
      {franchisesReport && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
            <Analytics sx={{ color: "var(--primary-color)" }} />
            Análise Geral das Unidades
          </Typography>
          <FranchiseAnalyticsCards report={franchisesReport} />
          <TopFranchisesCards report={franchisesReport} />
        </Box>
      )}

      {/* Upload Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Atualizar Unidades via CSV
        </Typography>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={{ display: 'none' }}
            ref={fileInputRef}
          />

          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => fileInputRef.current?.click()}
          >
            Selecionar CSV
          </Button>

          <Button
            variant="text"
            startIcon={<Download />}
            onClick={downloadCSVTemplate}
          >
            Baixar Modelo CSV
          </Button>
        </Box>
      </Paper>

      <FranchisesList
        franchises={franchises}
        franchisesReport={franchisesReport}
        onFranchiseClick={handleFranchiseClick}
      />

      <PreviewDialog
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        previewData={previewData}
        onConfirm={handleUpload}
        isUploading={isUploading}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <FranchiseDetailsModal
        isOpen={showDetailsModal}
        onClose={handleCloseDetailsModal}
        communityId={communityId}
        franchiseId={selectedFranchiseId}
      />
    </Box>
  );
};

export const processCSVFile = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;

        // Split by lines and clean up
        const lines = csvContent.split('\n')
          .map(line => line.trim())
          .filter(line => line.length > 0);

        if (lines.length === 0) {
          reject(new Error("Arquivo CSV está vazio"));
          return;
        }

        let franchiseNames = [];

        // Check if first line is a header
        const firstLineValues = lines[0].split(';').map(val => val.trim().replace(/[";]/g, ''));
        const hasHeader = firstLineValues.some(val => val.toLowerCase() === 'name' || val.toLowerCase() === 'nome');

        const dataLines = hasHeader ? lines.slice(1) : lines;

        dataLines.forEach(line => {
          const values = line.split(';').map(val => val.trim().replace(/[";]/g, ''));
          const name = values.find(val => val.length > 0);
          if (name) {
            franchiseNames.push(name);
          }
        });

        // Remove duplicates and empty entries
        const uniqueNames = [...new Set(franchiseNames.filter(name => name.length > 0))];

        if (uniqueNames.length === 0) {
          reject(new Error("Nenhum nome de unidade válido encontrado no arquivo"));
          return;
        }

        resolve({
          franchiseNames: uniqueNames,
          hasHeader,
          totalLines: lines.length,
          processedLines: dataLines.length
        });

      } catch (error) {
        reject(new Error("Erro ao processar arquivo CSV: " + error.message));
      }
    };

    reader.onerror = () => {
      reject(new Error("Erro ao ler o arquivo"));
    };

    reader.readAsText(file);
  });
};
